import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  getLocalVehicleImage,
  LOCAL_VEHICLES,
} from "../data/vehicles";

const VehiclesContext = createContext({
  vehicles: [],
  loading: true,
  error: null,
});

const withLocalImages = (list) =>
  list.map((vehicle) => ({
    ...vehicle,
    vehicleName: String(vehicle.vehicleName || "").trim(),
    imageUrl: getLocalVehicleImage(vehicle.vehicleName),
  }));

export const VehiclesProvider = ({ children }) => {
  // Show local fleet immediately so hero images don't wait on Firestore.
  const [vehicles, setVehicles] = useState(() => withLocalImages(LOCAL_VEHICLES));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const fetchVehicles = async () => {
      try {
        setLoading(true);
        setError(null);
        const snapshot = await getDocs(collection(db, "vehicles"));
        if (!active) return;

        const fromFirestore = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (fromFirestore.length > 0) {
          setVehicles(withLocalImages(fromFirestore));
        }
      } catch (err) {
        if (active) {
          console.error("Error fetching vehicles:", err);
          setError(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchVehicles();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(
    () => ({ vehicles, loading, error }),
    [vehicles, loading, error]
  );

  return (
    <VehiclesContext.Provider value={value}>{children}</VehiclesContext.Provider>
  );
};

export const useVehicles = () => useContext(VehiclesContext);
